// Conversation roles (docs/adr/OPEN-15, OPEN-37). A role is three things: the prompt file it renders, what it may do with
// memory, and the audience it serves. The current role opens the prefix; explicit switches start a new prompt segment (OPEN-42).
import type { Audience, ConversationRole, ProjectKind, StateDocKind } from './domain.js';

export interface RoleDefinition {
  label: string;
  description: string;
  /** File under prompts/ (next to GLOBAL_PROMPT_PATH). product_owner keeps global.md so pinned versions stay valid. */
  promptFile: string;
  /** 'read-write': reads both scopes, writes both. 'user-only': reads both, writes only what it learns about the person. */
  memory: 'read-write' | 'user-only';
  audience: Audience;
}

export const ROLES: Record<ConversationRole, RoleDefinition> = {
  product_owner: {
    label: 'Product owner',
    description: 'Probes the goal, surfaces assumptions and contradictions, and works toward requirements.',
    promptFile: 'global.md',
    memory: 'read-write',
    audience: 'internal',
  },
  open: {
    label: 'Open',
    description: 'A general thinking partner for learning, planning and exploring, without the product owner rules.',
    promptFile: 'open.md',
    memory: 'user-only',
    audience: 'internal',
  },
  sales_executive: {
    label: 'Sales executive',
    description: 'Listens for buyer goals, constraints, objections and agreed next steps.',
    promptFile: 'sales.md', memory: 'read-write', audience: 'internal',
  },
  client_sales_executive: {
    label: 'Sales executive (production)',
    description: 'A sales partner using only the authorized production picture.',
    promptFile: 'sales.md', memory: 'user-only', audience: 'client',
  },
  // The role every guest member's conversation runs on. Guests are full users who joined via invite:
  // the owner adds them by email on the existing magic-link path (the Communication Services mailer),
  // and audience 'client' keeps them to the production state document by construction.
  client_partner: {
    label: 'Client partner',
    description: 'A courteous product partner for clients, grounded in the production picture.',
    promptFile: 'client.md',
    memory: 'user-only',
    audience: 'client',
  },
  // Beyond code (MASTER-PLAN Phase 6 item 2, docs/adr/OPEN-37): a marketing or strategy conversation works
  // toward a draft, the same way the product owner works toward requirements. It reads and writes project
  // and user memory like the product owner, and its audience is internal only: a marketing change order or
  // a strategy memo is never shown to a guest, and neither role ever gets a client-audience conversation.
  marketing: {
    label: 'Marketing',
    description: 'Thinks through positioning, messaging and campaigns, and drafts a brief for a person to carry into Yard as a change order.',
    promptFile: 'marketing.md',
    memory: 'read-write',
    audience: 'internal',
  },
  strategy: {
    label: 'Strategy',
    description: 'Thinks through options, trade-offs and direction, and drafts a decision memo or brief for a person to act on.',
    promptFile: 'strategy.md',
    memory: 'read-write',
    audience: 'internal',
  },
};

/** My space opens in the open role; every project (private or shared) opens with the product owner. */
export function defaultRoleFor(kind: ProjectKind): ConversationRole {
  return kind === 'personal' ? 'open' : 'product_owner';
}

/**
 * The state documents an audience may be shown. A client sees production and nothing else: the dev state,
 * the backlog and raw feedback never have a path into a client conversation.
 */
export function stateKindsForAudience(audience: Audience): StateDocKind[] {
  return audience === 'client' ? ['production'] : ['production', 'dev', 'backlog', 'feedback'];
}
